import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/App';

function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(true);
  const [success, setSuccess] = useState(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      navigate('/dashboard');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your dashboard.');
      navigate('/dashboard');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.get(`${API}/payments/status/${sessionId}`, config);

      if (response.data.payment_status === 'paid') {
        setSuccess(true);
        setChecking(false);
        toast.success('Payment successful! Welcome to Radiant 40! 🌸');
        return;
      }

      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      toast.error('Error checking payment status');
      navigate('/dashboard');
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-secondary-50 to-accent-50 flex items-center justify-center px-6">
        <div className="text-center">
          <Sparkles className="w-20 h-20 text-primary animate-pulse mx-auto mb-6" />
          <h2 className="text-3xl font-serif font-bold text-secondary-900 mb-3">
            Processing your payment...
          </h2>
          <p className="text-secondary-600">This will only take a moment</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-secondary-50 to-accent-50 flex items-center justify-center px-6">
        <div className="max-w-2xl text-center">
          <div className="bg-accent/10 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-16 h-16 text-accent" />
          </div>
          <h1 className="text-5xl font-serif font-bold text-secondary-900 mb-4">
            Welcome to Radiant 40!
          </h1>
          <p className="text-xl text-secondary-700 mb-8">
            Your payment was successful. Your transformation journey begins now!
          </p>
          <div className="bg-white rounded-2xl p-8 shadow-xl mb-8">
            <h3 className="text-2xl font-serif font-bold text-secondary-900 mb-4">What's Next?</h3>
            <ul className="text-left space-y-3 text-secondary-700">
              <li className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 text-primary shrink-0 mt-1" />
                <span>Explore your personalized 12-week program</span>
              </li>
              <li className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 text-primary shrink-0 mt-1" />
                <span>Complete daily tasks to grow your beautiful garden</span>
              </li>
              <li className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 text-primary shrink-0 mt-1" />
                <span>Track your progress and celebrate your transformation</span>
              </li>
            </ul>
          </div>
          <Button
            data-testid="go-to-dashboard-button"
            onClick={() => navigate('/dashboard')}
            className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Go to Dashboard <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    );
  }

  return null;
}

export default PaymentSuccess;